// ========================
// USERS & PERMISSIONS (V3 — DB-backed, multi-tenant)
// ========================

import type { Tenant } from './tenant'
import type { Client } from './campaign'

/** Discriminator: define qual extension (team_members ou client_profiles) existe. */
export type UserType = 'team_member' | 'client_user'

/** Internal team roles. Determina permissions + display label. */
export type TeamRole = 'admin' | 'manager' | 'coordinator' | 'assistant'

/** All possible roles across user types. */
export type Role = TeamRole | 'client'

export type UserStatus = 'active' | 'pending' | 'inactive'

/** Display labels PT-BR pros roles. */
export const ROLE_LABELS: Record<Role, string> = {
  admin:       'Admin',
  manager:     'Gestor',
  coordinator: 'Coordenadora',
  assistant:   'Assistente',
  client:      'Cliente',
}

/** Base user identity (1:1 com auth.users). */
export interface AppUser {
  id: string
  tenant_id: string
  email: string
  name: string
  avatar_color?: string | null
  status: UserStatus
  user_type: UserType
  invited_at?: string | null
  activated_at?: string | null
  created_at: string
  updated_at: string
}

/** Extension pra user_type='team_member'. */
export interface TeamMember {
  user_id: string
  tenant_id: string
  role: TeamRole
  leader_id?: string | null
  created_at: string
  updated_at: string
}

/** Extension pra user_type='client_user'. */
export interface ClientProfile {
  user_id: string
  tenant_id: string
  client_id: string
  created_at: string
  updated_at: string
}

/** Associação N:N team_member ↔ campaign. */
export interface TeamMemberCampaign {
  team_member_id: string
  campaign_id: string
  tenant_id: string
  role_in_campaign: 'coordinator' | 'assistant'
  created_at: string
}

/** Associação N:N client_user ↔ campaign. */
export interface ClientUserCampaign {
  client_user_id: string
  campaign_id: string
  tenant_id: string
  created_at: string
}

/** Matriz de permissões (role × permission_key → granted). */
export interface RolePermission {
  tenant_id: string
  role: Role
  permission_key: FeaturePermissionKey
  granted: boolean
  updated_at: string
}

/** Feature permission keys. 14 originais (features) + 10 de navegação (ver_*). */
export type FeaturePermissionKey =
  // Features
  | 'criar_campanha'
  | 'criar_entrega'
  | 'enviar_whatsapp'
  | 'ver_financeiro'
  | 'gerar_relatorio'
  | 'convidar_pessoas'
  | 'editar_briefing'
  | 'excluir_entrega'
  | 'aprovar_roteiro'
  | 'aprovar_preview'
  | 'pedir_ajuste'
  | 'ver_pagamentos'
  | 'exportar_pagamentos'
  | 'comentar_entregas'
  // Navegação (controla visibilidade de cada aba da sidebar)
  | 'ver_dashboard'
  | 'ver_campanhas'
  | 'ver_influenciadores'
  | 'ver_clientes'
  | 'ver_cronograma'
  | 'ver_relatorios'
  | 'ver_performance'
  | 'ver_equipe'
  | 'ver_configuracoes'
  | 'ver_configuracoes_agencia'
  | 'ver_aprovacoes'
  | 'ver_pagamentos_aba'  // distinto de ver_pagamentos (feature) — controla se aba aparece na sidebar
  | 'ver_curadoria'

export const FEATURE_PERMISSION_LABELS: Record<FeaturePermissionKey, string> = {
  // Features
  criar_campanha:      'Criar campanha',
  criar_entrega:       'Criar entrega',
  enviar_whatsapp:     'Enviar WhatsApp',
  ver_financeiro:      'Ver financeiro',
  gerar_relatorio:     'Gerar relatório',
  convidar_pessoas:    'Convidar pessoas',
  editar_briefing:     'Editar briefing',
  excluir_entrega:     'Excluir entrega',
  aprovar_roteiro:     'Aprovar roteiro',
  aprovar_preview:     'Aprovar preview',
  pedir_ajuste:        'Pedir ajuste',
  ver_pagamentos:      'Ver pagamentos',
  exportar_pagamentos: 'Exportar pagamentos',
  comentar_entregas:   'Comentar entregas',
  // Navegação
  ver_dashboard:       'Dashboard',
  ver_campanhas:       'Campanhas',
  ver_influenciadores: 'Influenciadores',
  ver_clientes:        'Clientes',
  ver_cronograma:      'Cronograma',
  ver_relatorios:      'Relatórios',
  ver_performance:     'Performance',
  ver_equipe:          'Pessoas e Funcionalidades',
  ver_configuracoes:   'Configurações',
  ver_configuracoes_agencia: 'Configurações da agência',
  ver_aprovacoes:      'Aprovações',
  ver_pagamentos_aba:  'Pagamentos',
  ver_curadoria:       'Curadoria',
}

/** Todas as 14 keys de feature em ordem. */
export const FEATURE_PERMISSION_KEYS: FeaturePermissionKey[] = [
  'criar_campanha', 'criar_entrega', 'enviar_whatsapp', 'ver_financeiro',
  'gerar_relatorio', 'convidar_pessoas', 'editar_briefing', 'excluir_entrega',
  'aprovar_roteiro', 'aprovar_preview', 'pedir_ajuste', 'ver_pagamentos',
  'exportar_pagamentos', 'comentar_entregas',
]

/** Navigation permission keys — quais abas cada role vê. */
export const SCREEN_PERMISSION_KEYS: FeaturePermissionKey[] = [
  'ver_dashboard', 'ver_campanhas', 'ver_influenciadores', 'ver_clientes',
  'ver_curadoria', 'ver_cronograma', 'ver_relatorios', 'ver_performance', 'ver_equipe',
  'ver_configuracoes', 'ver_configuracoes_agencia', 'ver_aprovacoes',
  'ver_pagamentos_aba',
]

/** Current user profile — lido no login via loadOwnProfile (features/auth/lib/load-profile.ts). */
export interface CurrentUserProfile {
  user: AppUser
  tenant: Tenant
  /** Definido se user_type='team_member'. */
  team?: TeamMember
  /** Definido se user_type='client_user'. */
  client?: ClientProfile & { client_ref?: Client }
  /** Campanhas que este user tem acesso (derivado de team_member_campaigns OR
   *  client_user_campaigns conforme user_type; admin/manager vê todas do tenant). */
  campaign_ids: string[]
  /** Permissions resolvidas (role × features). */
  permissions: Partial<Record<FeaturePermissionKey, boolean>>
  /** True se user está em public.platform_admins (operador da plataforma Runna). */
  is_platform_admin: boolean
}
